import { AutoColumn } from 'components/Column'
import PoweredByKodiak from 'components/PoweredByKodiak'
import Row from 'components/Row'
import { SearchIcon, SearchInput } from 'components/SearchModal/styleds'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import { isSupportedChain, SupportedChainId } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import { useCallback, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { useLocation } from 'react-router-dom'
import { useToggleWalletModal } from 'state/application/hooks'
import { useSearchTerm as useFarmSearchTerm, useSetFarmSearchTerm } from 'state/kodiakFarms/hooks'
import {
  useSearchTerm as useIslandSearchTerm,
  useSetSearchTerm as useSetIslandSearchTerm,
} from 'state/kodiakIslands/hooks'
import { useTheme } from 'styled-components'
import { ChainAwareLink, ThemedText } from 'theme'

import * as S from './Liquidity.styles'
import { RefreshButton } from './Liquidity.styles'
import FarmHeader from './ProtocolHeaders/FarmHeader'
import IslandHeader from './ProtocolHeaders/IslandHeader'
import V2Header from './ProtocolHeaders/V2Header'
import V3Header from './ProtocolHeaders/V3Header'
import Farms from './ProtocolLists/farms'
import Islands from './ProtocolLists/islands'
import PoolV2 from './ProtocolLists/v2'
import PoolV3 from './ProtocolLists/v3'
function WrongNetworkCard() {
  const theme = useTheme()
  return (
    <>
      <S.PageWrapper style={{ padding: 0 }}>
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <S.MainContentWrapper>
            <S.ErrorContainer>
              <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                <S.NetworkIcon strokeWidth={1.2} />
                <div data-testid="pools-unsupported-err">
                  <>Your connected network is unsupported.</>
                </div>
              </ThemedText.DeprecatedBody>
            </S.ErrorContainer>
          </S.MainContentWrapper>
        </AutoColumn>
      </S.PageWrapper>
    </>
  )
}

export interface RefreshControl {
  onRegister: (callback: (() => Promise<void>) | null) => void
  isRefreshing: boolean
}

const Liquidity = () => {
  const location = useLocation()
  const { account, chainId } = useConnection()
  const toggleWalletModal = useToggleWalletModal()

  const islandSearchTerm = useIslandSearchTerm()
  const setIslandSearchTerm = useSetIslandSearchTerm()
  const farmSearchTerm = useFarmSearchTerm()
  const setFarmSearchTerm = useSetFarmSearchTerm()

  const isFarmsTab = location.pathname.includes('/liquidity/farms')
  const searchTerm = isFarmsTab ? farmSearchTerm : islandSearchTerm
  const setSearchTerm = isFarmsTab ? setFarmSearchTerm : setIslandSearchTerm

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshCallback, setRefreshCallback] = useState<(() => Promise<void>) | null>(null)

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleRefresh = useCallback(async () => {
    if (!refreshCallback || isRefreshing) return

    setIsRefreshing(true)
    try {
      await refreshCallback()
    } catch (error) {
      console.error('Refresh failed:', error)
    } finally {
      setTimeout(() => setIsRefreshing(false), 500)
    }
  }, [refreshCallback, isRefreshing])

  const registerRefresh = useCallback((callback: (() => Promise<void>) | null) => {
    setRefreshCallback(() => callback)
  }, [])

  const renderHeader = () => {
    if (location.pathname.includes('/v3')) return <V3Header />
    if (location.pathname.includes('/v2')) return <V2Header />
    if (location.pathname.includes('farms')) return <FarmHeader />
    return <IslandHeader />
  }

  const renderBody = () => {
    const refreshControl = {
      onRegister: registerRefresh,
      isRefreshing,
    }

    if (location.pathname.includes('/liquidity/v3')) {
      return (
        <PoolV3
          account={account}
          chainId={chainId}
          toggleWalletModal={toggleWalletModal}
          searchParam={searchTerm}
          refreshControl={refreshControl}
        />
      )
    } else if (location.pathname.includes('/liquidity/v2')) {
      return (
        <PoolV2
          account={account}
          toggleWalletModal={toggleWalletModal}
          searchParam={searchTerm}
          refreshControl={refreshControl}
        />
      )
    } else if (location.pathname.includes('/liquidity/farms')) {
      return <Farms refreshControl={refreshControl} />
    } else {
      return <Islands refreshControl={refreshControl} />
    }
  }

  const renderNavbar = () => {
    return (
      <S.FilterFlex>
        <S.Switch>
          <ChainAwareLink to="/liquidity/pools">
            <S.Option active={location.pathname.includes('/liquidity/pools')}>
              <ThemedText.DeprecatedBody
                fontSize="12px !important"
                margin="0px 16px"
                lineHeight="20px"
                fontWeight="500"
              >
                Top Pools
              </ThemedText.DeprecatedBody>
            </S.Option>
          </ChainAwareLink>
          <ChainAwareLink to="/liquidity/v3">
            <S.Option active={location.pathname.includes('/liquidity/v3')}>
              {' '}
              <ThemedText.DeprecatedBody
                fontSize="12px !important"
                margin="0px 16px"
                lineHeight="20px"
                fontWeight="500"
              >
                Custom V3
              </ThemedText.DeprecatedBody>
            </S.Option>
          </ChainAwareLink>
          <ChainAwareLink to="/liquidity/v2">
            <S.Option active={location.pathname.includes('/liquidity/v2')}>
              {' '}
              <ThemedText.DeprecatedBody
                fontSize="12px !important"
                margin="0px 16px"
                lineHeight="20px"
                fontWeight="500"
              >
                Custom V2
              </ThemedText.DeprecatedBody>
            </S.Option>
          </ChainAwareLink>
          {chainId === SupportedChainId.BERA && (
            <ChainAwareLink to="/liquidity/farms">
              <S.Option active={location.pathname.includes('/liquidity/farms')}>
                <ThemedText.DeprecatedBody
                  fontSize="12px !important"
                  margin="0px 16px"
                  lineHeight="20px"
                  fontWeight="500"
                >
                  Farms
                </ThemedText.DeprecatedBody>
              </S.Option>
            </ChainAwareLink>
          )}
        </S.Switch>
        <>
          <S.SearchWrapper>
            <Row gap="6px">
              <SearchIcon height={20} width={20} />
              <SearchInput placeholder="Enter token symbol" onChange={handleSearch} value={searchTerm} />
              <RefreshButton
                onClick={handleRefresh}
                disabled={!refreshCallback || isRefreshing}
                isRefreshing={isRefreshing}
                title="Refresh data"
              >
                <RefreshCw size={16} />
              </RefreshButton>
            </Row>
          </S.SearchWrapper>
        </>
      </S.FilterFlex>
    )
  }

  return (
    <S.PageWrapper>
      {renderHeader()}
      {renderNavbar()}
      {account && !isSupportedChain(chainId) ? <WrongNetworkCard /> : renderBody()}
      <PoweredByKodiak />
      <SwitchLocaleLink />
    </S.PageWrapper>
  )
}

export default Liquidity
